import API_BASE from "./apiConfig";
import React, { useState, useEffect } from "react";
import {
  FaSearch, FaChild, FaEnvelope, FaPhone, FaKey, FaUser,
  FaPlus, FaSave, FaTimesCircle, FaCheckCircle,
  FaExclamationCircle, FaToggleOn, FaToggleOff,
  FaEdit, FaLock, FaShieldAlt, FaInfoCircle,
} from "react-icons/fa";
import { Colors } from "./branding";
const BASE = API_BASE;
const T = {
  glass:      "rgba(255,255,255,0.72)",
  glassBdr:   "1px solid rgba(255,255,255,0.55)",
  glassSh:    "0 8px 32px rgba(0,40,100,0.10)",
  blur:       "blur(18px)",
  cardR:      "18px",
  inputR:     "10px",
  yellow:     Colors.logoYellow  || "#f7ce0b",
  blue:       Colors.brandBlue   || "#034e7b",
  blueDark:   Colors.primaryBlue || "#023a5c",
  gray:       Colors.iconGray    || "#8899aa",
  inputBg:    "rgba(255,255,255,0.82)",
  font:       "'Segoe UI', system-ui, -apple-system, sans-serif",
};

// ─── Shared style atoms ────────────────────────────────────────────────────────
const card    = { background: T.glass, backdropFilter: T.blur, WebkitBackdropFilter: T.blur, border: T.glassBdr, borderRadius: T.cardR, boxShadow: T.glassSh, padding: "22px 26px", boxSizing: "border-box" };
const yBtn    = { padding: "10px 20px", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", color: T.blue, fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, backgroundImage: `linear-gradient(135deg,${T.yellow},#fff3a0)`, boxShadow: "0 4px 14px rgba(255,200,0,0.35)" };
const bBtn    = { padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "white", fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "rgba(2,58,92,0.82)", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 14px rgba(2,58,92,0.28)" };
const dBtn    = { padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 7, background: "rgba(220,0,0,0.07)", border: "1px solid rgba(220,0,0,0.20)", color: "#c0392b" };
const labelSt = { display: "block", marginBottom: 7, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.blue, fontFamily: T.font };
const wrapSt  = { display: "flex", alignItems: "center", border: "1.5px solid rgba(0,53,128,0.13)", borderRadius: T.inputR, padding: "10px 14px", background: T.inputBg, backdropFilter: T.blur, WebkitBackdropFilter: T.blur, boxShadow: "0 2px 8px rgba(0,40,100,0.06)", width: "100%", boxSizing: "border-box" };
const inpSt   = { border: "none", outline: "none", width: "100%", background: "transparent", fontSize: 14, color: "#1a2840", fontFamily: T.font };
const toast   = (ok) => ({ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600, fontFamily: T.font, background: ok ? "rgba(212,237,218,0.92)" : "rgba(248,215,218,0.92)", color: ok ? "#155724" : "#721c24", border: ok ? "1px solid #b8dfc4" : "1px solid #f3b8bc", backdropFilter: T.blur });

function Divider({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0 16px" }}>
      <div style={{ height: 2, width: 18, background: T.yellow, borderRadius: 2 }} />
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: T.gray, fontFamily: T.font }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: "rgba(0,53,128,0.08)" }} />
    </div>
  );
}

function Field({ label, icon: Icon, name, type = "text", value, onChange, placeholder, error, readOnly, hint }) {
  return (
    <div>
      <label style={labelSt}>{label}</label>
      <div style={{ ...wrapSt, background: readOnly ? "rgba(0,53,128,0.04)" : T.inputBg }}>
        {Icon && <Icon size={14} style={{ marginRight: 10, color: T.gray, flexShrink: 0 }} />}
        <input type={type} name={name} value={value} onChange={onChange}
          placeholder={placeholder} readOnly={readOnly} style={{ ...inpSt, cursor: readOnly ? "default" : "text" }} />
      </div>
      {hint  && <p style={{ fontSize: 11, color: T.gray,     margin: "4px 0 0", fontFamily: T.font }}>{hint}</p>}
      {error && <p style={{ fontSize: 11, color: "#c0392b",  margin: "4px 0 0", fontFamily: T.font, display: "flex", alignItems: "center", gap: 4 }}><FaExclamationCircle size={10} /> {error}</p>}
    </div>
  );
}

const blankForm = { full_name: "", email: "", phone: "", password: "", confirm_password: "" };

// ══════════════════════════════════════════════════════════════════════════════
export default function ParentAccessPanel({ currentUserId }) {
  // ── Student search state ──────────────────────────────────────────────────
  const [query,      setQuery]      = useState("");
  const [results,    setResults]    = useState([]);
  const [student,    setStudent]    = useState(null);   // selected student full info
  const [accounts,   setAccounts]   = useState([]);
  const [loading,    setLoading]    = useState(false);

  // ── Panel state ───────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [resetId,    setResetId]    = useState(null);
  const [form,       setForm]       = useState(blankForm);
  const [editForm,   setEditForm]   = useState({});
  const [resetPwd,   setResetPwd]   = useState({ p: "", c: "" });
  const [errors,     setErrors]     = useState({});
  const [msg,        setMsg]        = useState(null);

  // ── Debounced student search ──────────────────────────────────────────────
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BASE}/students/search?name=${encodeURIComponent(query)}`);
        const d = await r.json();
        setResults(d.students || []);
      } catch { setResults([]); }
    }, 280);
    return () => clearTimeout(t);
  }, [query]);

  // ── Load student + accounts ───────────────────────────────────────────────
  const loadStudent = async (s) => {
    // Guard: ensure student object has valid name before setting query
    const name = (s?.first_name && s?.last_name)
      ? `${s.first_name} ${s.last_name}`
      : s?.first_name || s?.last_name || "";
    setQuery(name);
    setResults([]);
    setLoading(true);
    setShowCreate(false); setEditId(null); setResetId(null);
    try {
      const r = await fetch(`${BASE}/parent-accounts/student/${s.student_id}`);
      const d = await r.json();
      setStudent(d.student);
      setAccounts(d.accounts || []);
    } catch { setStudent(null); setAccounts([]); }
    finally { setLoading(false); }
  };

  const reloadAccounts = async () => {
    if (!student) return;
    const r = await fetch(`${BASE}/parent-accounts/student/${student.student_id}`);
    const d = await r.json();
    setAccounts(d.accounts || []);
    // refresh student too so parent_email badge updates
    if (d.student) setStudent(d.student);
  };

  const showMsg = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); };

  // ── Open create form — pre-fill from student's parent_email ──────────────
  const openCreate = () => {
    setForm({
      ...blankForm,
      full_name: student?.parent_name || "",
      email:     student?.parent_email || "",
      phone:     student?.parent_phone || "",
    });
    setErrors({});
    setShowCreate(true);
    setEditId(null); setResetId(null);
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const errs = {};
    if (!form.full_name.trim())               errs.full_name       = "Required";
    if (!form.email || !form.email.includes("@")) errs.email       = "Valid email required";
    if (!form.password || form.password.length < 6) errs.password  = "Min 6 characters";
    if (form.password !== form.confirm_password)    errs.confirm_password = "Passwords do not match";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    try {
      const r = await fetch(`${BASE}/parent-accounts/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: student.student_id,
          full_name:  form.full_name,
          email:      form.email,
          phone:      form.phone,
          password:   form.password,
          created_by: String(currentUserId || "system"),
        }),
      });
      const d = await r.json();
      showMsg(d.success, d.message);
      if (d.success) { setShowCreate(false); setForm(blankForm); reloadAccounts(); }
    } catch (e) { showMsg(false, "Network error: " + e.message); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (acc) => {
    setEditId(acc.parent_id);
    setEditForm({ full_name: acc.full_name, email: acc.email, phone: acc.phone || "", active_status: acc.active_status });
    setShowCreate(false); setResetId(null);
  };

  const handleUpdate = async () => {
    try {
      const r = await fetch(`${BASE}/parent-accounts/${editId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editForm, updated_by: String(currentUserId || "system") }),
      });
      const d = await r.json();
      showMsg(d.success, d.message);
      if (d.success) { setEditId(null); reloadAccounts(); }
    } catch (e) { showMsg(false, "Network error: " + e.message); }
  };

  // ── Reset password ────────────────────────────────────────────────────────
  const handleResetPwd = async (parentId) => {
    if (!resetPwd.p || resetPwd.p.length < 6) { showMsg(false, "Password must be at least 6 characters."); return; }
    if (resetPwd.p !== resetPwd.c) { showMsg(false, "Passwords do not match."); return; }
    try {
      const r = await fetch(`${BASE}/parent-accounts/${parentId}/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: resetPwd.p, updated_by: String(currentUserId || "system") }),
      });
      const d = await r.json();
      showMsg(d.success, d.message);
      if (d.success) { setResetId(null); setResetPwd({ p: "", c: "" }); }
    } catch (e) { showMsg(false, "Network error: " + e.message); }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────
  const handleToggle = async (acc) => {
    const next  = acc.active_status ? 0 : 1;
    const label = next ? "activate" : "deactivate";
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} this parent account?`)) return;
    try {
      const r = await fetch(`${BASE}/parent-accounts/${acc.parent_id}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_status: next }),
      });
      const d = await r.json();
      showMsg(d.success, d.message);
      if (d.success) reloadAccounts();
    } catch (e) { showMsg(false, "Network error: " + e.message); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.font, minHeight: 400 }}>

      {/* ── STEP 1: Student Search ── */}
      <div style={{ ...card, marginBottom: 20, borderTop: `3px solid ${T.yellow}` }}>
        <Divider>Step 1 — Search &amp; Select Student</Divider>
        <div style={{ position: "relative" }}>
          <div style={{ ...wrapSt, background: "rgba(255,255,255,0.90)" }}>
            <FaSearch size={14} style={{ marginRight: 10, color: T.gray, flexShrink: 0 }} />
            <input
              value={query}
              onChange={e => { setQuery(e.target.value); if (!e.target.value) { setStudent(null); setAccounts([]); }}}
              placeholder="Type student first or last name…"
              style={inpSt}
            />
            {student && (
              <button onClick={() => { setQuery(""); setStudent(null); setAccounts([]); setShowCreate(false); setEditId(null); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: T.gray, padding: 0 }}>
                <FaTimesCircle size={14} />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {results.length > 0 && !student && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "rgba(255,255,255,0.98)", backdropFilter: T.blur, border: T.glassBdr, borderRadius: 12, boxShadow: "0 8px 24px rgba(3,78,123,0.16)", maxHeight: 220, overflowY: "auto" }}>
              {results.map((s, i) => (
                <div key={s.student_id} onClick={() => loadStudent(s)}
                  style={{ padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontFamily: T.font, borderBottom: i < results.length - 1 ? "1px solid rgba(0,53,128,0.07)" : "none" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,200,0,0.14)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg,${T.yellow},#fff3a0)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: T.blue, flexShrink: 0 }}>
                    {s.first_name?.[0]}{s.last_name?.[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: T.blue }}>{s.first_name} {s.last_name}</div>
                    <div style={{ fontSize: 11, color: T.gray }}>Student ID #{s.student_id}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected student card */}
        {student && (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 16, padding: "14px 18px", borderRadius: 12, background: `linear-gradient(120deg,${T.blueDark},${T.blue})`, border: "none" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: `rgba(255,255,255,0.15)`, border: `2px solid ${T.yellow}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "white", overflow: "hidden" }}>
              {student.photo_url
                ? <img src={`${BASE}/${student.photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : `${student.first_name?.[0] || ""}${student.last_name?.[0] || ""}`}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "white" }}>{student.first_name} {student.last_name}</div>
              <div style={{ display: "flex", gap: 14, marginTop: 4, flexWrap: "wrap" }}>
                {[
                  { label: "ID",     val: `#${student.student_id}` },
                  { label: "Parent", val: student.parent_name || "—" },
                  { label: "Email on file", val: student.parent_email || "Not set" },
                ].map(i => (
                  <div key={i.label} style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", fontFamily: T.font }}>
                    <span style={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{i.label}: </span>
                    <span style={{ fontWeight: 700, color: i.label === "Email on file" && !student.parent_email ? "rgba(255,180,0,0.80)" : "rgba(255,255,255,0.85)" }}>{i.val}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Parent role badge */}
            <div style={{ padding: "5px 14px", borderRadius: 20, background: `rgba(247,206,11,0.18)`, border: `1px solid ${T.yellow}60`, fontSize: 11, fontWeight: 800, color: T.yellow, letterSpacing: "0.06em", flexShrink: 0 }}>
              PARENT PORTAL
            </div>
          </div>
        )}
      </div>

      {/* ── STEP 2: Portal Access management — only shown once student selected ── */}
      {student && (
        <div style={{ ...card, borderTop: `3px solid ${T.blue}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <Divider>Step 2 — Parent Portal Access</Divider>
            {!showCreate && (
              <button onClick={openCreate} style={{ ...yBtn, marginLeft: 16, whiteSpace: "nowrap", flexShrink: 0, marginBottom: 6 }}>
                <FaPlus size={12} /> Add Parent Account
              </button>
            )}
          </div>

          {/* Info hint when parent_email is missing */}
          {!student.parent_email && (
            <div style={{ display: "flex", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 14, background: "rgba(255,180,0,0.10)", border: "1px solid rgba(255,180,0,0.30)", fontSize: 12, color: "#7a4800", fontFamily: T.font }}>
              <FaInfoCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              No parent email is set on this student record. The email you enter below will become the login email <strong>and</strong> will be saved to the student's Parent Email field automatically.
            </div>
          )}

          {/* Toast */}
          {msg && <div style={toast(msg.ok)}>{msg.ok ? <FaCheckCircle size={13} /> : <FaExclamationCircle size={13} />} {msg.text}</div>}

          {/* ── Create form ── */}
          {showCreate && (
            <div style={{ marginBottom: 20, padding: "18px 20px", borderRadius: 14, background: "rgba(247,206,11,0.07)", border: "1px solid rgba(247,206,11,0.25)" }}>
              <Divider>New Parent Account for {student.first_name} {student.last_name}</Divider>

              {/* Role badge — always Parent */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 14px", borderRadius: 20, marginBottom: 16, background: "rgba(3,78,123,0.08)", border: "1px solid rgba(3,78,123,0.20)" }}>
                <FaShieldAlt size={11} style={{ color: T.blue }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: T.blue, letterSpacing: "0.06em", fontFamily: T.font }}>ROLE: PARENT</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 24px" }}>
                <Field label="Full Name" icon={FaUser} name="full_name"
                  value={form.full_name} error={errors.full_name}
                  onChange={e => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Parent's full name" />

                <Field label="Login Email" icon={FaEnvelope} name="email" type="email"
                  value={form.email} error={errors.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="parent@email.com"
                  hint={student.parent_email ? `Pre-filled from student's parent email` : "This will also be saved to the student record"} />

                <Field label="Phone (optional)" icon={FaPhone} name="phone"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="000-000-0000" />

                <div /> {/* spacer */}

                <Field label="Password" icon={FaKey} name="password" type="password"
                  value={form.password} error={errors.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 6 characters" />

                <Field label="Confirm Password" icon={FaKey} name="confirm_password" type="password"
                  value={form.confirm_password} error={errors.confirm_password}
                  onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                  placeholder="Re-enter password" />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={handleCreate} style={yBtn}><FaSave size={13} /> Create Account</button>
                <button onClick={() => { setShowCreate(false); setErrors({}); }} style={bBtn}><FaTimesCircle size={13} /> Cancel</button>
              </div>
            </div>
          )}

          {/* ── Accounts list ── */}
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: T.gray, fontSize: 13 }}>Loading…</div>
          ) : accounts.length === 0 && !showCreate ? (
            <div style={{ padding: "28px 20px", textAlign: "center", borderRadius: 12, background: "rgba(0,53,128,0.04)", border: "1px dashed rgba(0,53,128,0.15)", color: T.gray, fontSize: 13, fontFamily: T.font }}>
              No parent accounts yet for this student.<br />
              <span style={{ color: T.blue, fontWeight: 700 }}>Click "Add Parent Account" above to create one.</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {accounts.map(acc => {
                const active    = acc.active_status === 1 || acc.active_status === "1";
                const isEditing = editId === acc.parent_id;
                const isReset   = resetId === acc.parent_id;

                return (
                  <div key={acc.parent_id} style={{ ...card, padding: "16px 20px", borderLeft: `4px solid ${active ? T.yellow : "rgba(220,0,0,0.30)"}` }}>

                    {/* Normal view */}
                    {!isEditing && !isReset && (
                      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        {/* Avatar */}
                        <div style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, background: active ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : "rgba(0,53,128,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 900, color: T.blue, border: `2px solid ${active ? "rgba(247,206,11,0.50)" : "rgba(0,53,128,0.15)"}` }}>
                          {acc.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: T.blue }}>{acc.full_name}</div>
                          <div style={{ fontSize: 12, color: T.gray, marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <span><FaEnvelope size={10} style={{ marginRight: 4 }} />{acc.email}</span>
                            {acc.phone && <span><FaPhone size={10} style={{ marginRight: 4 }} />{acc.phone}</span>}
                          </div>
                        </div>
                        {/* Role badge */}
                        <div style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 800, background: "rgba(3,78,123,0.08)", border: "1px solid rgba(3,78,123,0.18)", color: T.blue, letterSpacing: "0.05em", flexShrink: 0 }}>
                          PARENT
                        </div>
                        {/* Status */}
                        <div style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: active ? "rgba(39,174,96,0.10)" : "rgba(220,0,0,0.07)", border: active ? "1px solid rgba(39,174,96,0.25)" : "1px solid rgba(220,0,0,0.18)", color: active ? "#1a6e34" : "#b30000", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#27ae60" : "#e74c3c" }} />
                          {active ? "Active" : "Inactive"}
                        </div>
                        {/* Actions */}
                        <div style={{ display: "flex", gap: 7, flexShrink: 0 }}>
                          <button onClick={() => openEdit(acc)} style={{ ...bBtn, padding: "7px 13px", fontSize: 12 }}><FaEdit size={11} /> Edit</button>
                          <button onClick={() => { setResetId(acc.parent_id); setResetPwd({ p: "", c: "" }); setEditId(null); setShowCreate(false); }} style={{ ...bBtn, padding: "7px 13px", fontSize: 12, background: "rgba(52,73,94,0.72)" }}><FaLock size={11} /> Password</button>
                          <button onClick={() => handleToggle(acc)} style={{ ...dBtn, padding: "7px 13px", fontSize: 12 }}>
                            {active ? <><FaToggleOff size={11} /> Deactivate</> : <><FaToggleOn size={11} /> Activate</>}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Edit form */}
                    {isEditing && (
                      <div>
                        <Divider>Edit — {acc.full_name}</Divider>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 24px" }}>
                          <Field label="Full Name"    icon={FaUser}     name="full_name" value={editForm.full_name}    onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} />
                          <Field label="Login Email"  icon={FaEnvelope} name="email"     value={editForm.email}        onChange={e => setEditForm({ ...editForm, email: e.target.value })} type="email" />
                          <Field label="Phone"        icon={FaPhone}    name="phone"     value={editForm.phone}        onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                          <div>
                            <label style={labelSt}>Status</label>
                            <div style={wrapSt}>
                              <FaShieldAlt size={14} style={{ marginRight: 10, color: T.gray }} />
                              <select value={editForm.active_status} onChange={e => setEditForm({ ...editForm, active_status: Number(e.target.value) })} style={{ ...inpSt, cursor: "pointer" , colorScheme:"dark" }}>
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                          <button onClick={handleUpdate} style={yBtn}><FaSave size={13} /> Save Changes</button>
                          <button onClick={() => setEditId(null)} style={bBtn}><FaTimesCircle size={13} /> Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Reset password */}
                    {isReset && (
                      <div>
                        <Divider>Reset Password — {acc.full_name}</Divider>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 24px", maxWidth: 560 }}>
                          <Field label="New Password"     icon={FaKey} type="password" value={resetPwd.p} onChange={e => setResetPwd({ ...resetPwd, p: e.target.value })} placeholder="Min 6 characters" />
                          <Field label="Confirm Password" icon={FaKey} type="password" value={resetPwd.c} onChange={e => setResetPwd({ ...resetPwd, c: e.target.value })} placeholder="Re-enter password" />
                        </div>
                        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                          <button onClick={() => handleResetPwd(acc.parent_id)} style={yBtn}><FaKey size={13} /> Set New Password</button>
                          <button onClick={() => setResetId(null)} style={bBtn}><FaTimesCircle size={13} /> Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* No student selected yet */}
      {!student && !query && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: T.gray, fontSize: 13, fontFamily: T.font }}>
          <FaChild size={32} style={{ color: "rgba(3,78,123,0.15)", marginBottom: 12 }} />
          <div style={{ fontWeight: 700, color: T.blue, fontSize: 15, marginBottom: 6 }}>Parent Portal Access Manager</div>
          <div>Search for a student above to manage their parent login accounts.</div>
        </div>
      )}
    </div>
  );
}
